import { useState, useEffect } from "react";
import { leadsApi, chatbotApi, workspaceApi, formatApiError } from "../lib/api";
import { Checkbox } from "../components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Search, RefreshCw, Download, MoreHorizontal, Trash2, Loader2, Mail, Phone, User as UserIcon, Users, ChevronRight, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

/* Contacts. The people who left their details — a plain list you can read
   top to bottom, with everything else tucked behind Filters or the row menu. */

const STATUSES = [
  { k: "new", label: "New" },
  { k: "contacted", label: "Contacted" },
  { k: "qualified", label: "Qualified" },
  { k: "converted", label: "Converted" },
  { k: "lost", label: "Lost" },
];

function statusPillClass(status) {
  switch (status) {
    case "converted": return "bg-b2-livewash text-b2-live";
    case "contacted": return "bg-b2-attentionwash text-b2-attention";
    case "qualified": return "bg-b2-wash text-b2-washink";
    case "lost":      return "bg-black/[0.05] text-b2-faint";
    default:          return "bg-black/[0.04] text-b2-inksoft";
  }
}

function initialOf(lead) {
  const src = lead?.visitor_data?.name || lead?.visitor_data?.email || "";
  return src.trim() ? src.trim().charAt(0).toUpperCase() : "?";
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const statusCounts = { new: 0, contacted: 0, qualified: 0, converted: 0, lost: 0 };
  leads.forEach((l) => { const s = l.status || "new"; if (statusCounts[s] !== undefined) statusCounts[s] += 1; });
  const [search, setSearch] = useState("");
  const [taggedOnly, setTaggedOnly] = useState(false);
  const [filterBot, setFilterBot] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editLead, setEditLead] = useState(null);
  const [viewLead, setViewLead] = useState(null);
  const [leadChat, setLeadChat] = useState(null);
  const [leadChatLoading, setLeadChatLoading] = useState(false);
  const [editNotes, setEditNotes] = useState("");
  const [editTags, setEditTags] = useState("");
  const [sources, setSources] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState("");
  const [leadNotes, setLeadNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [filterBot, filterStatus]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!viewLead) { setLeadNotes([]); setNoteInput(""); return; }
    leadsApi.listNotes(viewLead.id).then((r) => setLeadNotes(r?.data?.notes || [])).catch(() => setLeadNotes([]));
  }, [viewLead]);

  const handleAddNote = async () => {
    const text = noteInput.trim();
    if (!text || !viewLead) return;
    setSavingNote(true);
    try {
      const r = await leadsApi.addNote(viewLead.id, text);
      setLeadNotes((prev) => [r.data, ...prev]);
      setNoteInput("");
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!viewLead) return;
    if (!window.confirm("Delete this note?")) return;
    try {
      await leadsApi.deleteNote(viewLead.id, noteId);
      setLeadNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
    } catch (e) {
      toast.error("Failed to delete note");
    }
  };


  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterBot !== "all") params.chatbot_id = filterBot;
      if (filterStatus !== "all") params.status = filterStatus;
      if (search) params.search = search;
      const [leadsRes, botsRes, sourcesRes, timelineRes, funnelRes, tagsRes] = await Promise.all([leadsApi.list(params), chatbotApi.list(), workspaceApi.leadSources().catch(() => ({ data: { items: [] } })), workspaceApi.leadsTimeline(30).catch(() => ({ data: { items: [] } })), workspaceApi.leadsFunnel(30).catch(() => ({ data: null })), workspaceApi.allTags().catch(() => ({ data: { leads: [], conversations: [] } }))]);
      setSources(sourcesRes?.data?.items || []);
    setTimeline(timelineRes?.data?.items || []);
    setFunnel(funnelRes?.data || null);
      setLeads(leadsRes.data.leads); setTotal(leadsRes.data.total); setChatbots(botsRes.data); setAllTags((tagsRes?.data?.lead_tags || tagsRes?.data?.leads) || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  const handleSearch = () => loadData();

  const handleStatusChange = async (lead, newStatus) => {
    try {
      const res = await leadsApi.update(lead.id, { status: newStatus });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: newStatus } : l)));
      setViewLead((v) => (v && v.id === lead.id ? { ...v, status: newStatus } : v));
      toast.success(`Marked as ${newStatus}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (lead) => {
    setEditLead(lead);
    setEditNotes(lead.notes || "");
    setEditTags((lead.tags || []).join(", "));
  };

  const saveEdit = async () => {
    if (!editLead) return;
    setSavingEdit(true);
    try {
      const tagsArr = editTags.split(",").map((t) => t.trim()).filter(Boolean);
      const res = await leadsApi.update(editLead.id, { notes: editNotes, tags: tagsArr });
      setLeads((prev) => prev.map((l) => (l.id === editLead.id ? { ...l, notes: editNotes, tags: tagsArr } : l)));
      toast.success("Lead updated");
      setEditLead(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const toggleSelect = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = (visibleIds) => setSelectedIds((s) => s.length === visibleIds.length ? [] : visibleIds);

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} lead${selectedIds.length>1?"s":""}? This cannot be undone.`)) return;
    try {
      const res = await leadsApi.bulkDelete(selectedIds);
      setLeads((prev) => prev.filter((l) => !selectedIds.includes(l.id)));
      setSelectedIds([]);
      toast.success(`Deleted ${res.data?.deleted ?? selectedIds.length} leads`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const shownLeads = taggedOnly ? leads.filter(l => (l.tags?.length || 0) > 0) : leads;
  const visibleIds = shownLeads.map(l => l.id);
  const allSelected = selectedIds.length > 0 && selectedIds.length === visibleIds.length;
  const filtersOn = filterBot !== "all" || filterStatus !== "all" || taggedOnly;
  const handleBulkTag = () => {
    if (!selectedIds.length) return;
    setBulkTagOpen(true);
    setBulkTagInput("");
  };

  const submitBulkTag = async () => {
    const addTags = bulkTagInput.split(",").map(t => t.trim()).filter(Boolean);
    if (!addTags.length) { setBulkTagOpen(false); return; }
    try {
      await leadsApi.bulkUpdate(selectedIds, { addTags });
      toast.success(`Tagged ${selectedIds.length} lead(s) with ${addTags.join(", ")}`);
      await loadData();
      setSelectedIds([]);
      setBulkTagOpen(false);
    } catch (e) {
      toast.error("Bulk tag failed");
    }
  };

  const handleBulkStatus = async (newStatus) => {
    if (!selectedIds.length || !newStatus) return;
    try {
      await leadsApi.bulkUpdate(selectedIds, { status: newStatus });
      toast.success(`Updated ${selectedIds.length} lead(s) to ${newStatus}`);
      setSelectedIds([]);
      loadData();
    } catch (e) { toast.error("Bulk status update failed"); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await leadsApi.remove(id); setLeads(leads.filter(l => l.id !== id)); toast.success("Lead deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
    if (filterBot !== "all") params.chatbot_id = filterBot;
    if (filterStatus !== "all") params.status = filterStatus;
      const res = await leadsApi.export(params);
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `leads-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported leads CSV");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setExporting(false); }
  };

  const getChatbotName = (id) => chatbots.find(c => c.id === id)?.name || "—";

  return (
    <div data-testid="leads-page">
      {/* -------------------------------- header -------------------------------- */}
      <div className="flex items-start justify-between gap-4 flex-col sm:flex-row sm:items-center">
        <div>
          <h1 className="text-[26px] leading-tight font-bold tracking-[-0.02em] text-b2-ink">Contacts</h1>
          <p className="mt-1.5 text-[14px] text-b2-muted">
            {total} {total === 1 ? "person has" : "people have"} shared their details.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={loadData}
            title="Refresh"
            data-testid="refresh-leads"
            className="h-9 w-9 rounded-[10px] border border-b2-line bg-b2-surface text-b2-muted hover:text-b2-ink flex items-center justify-center transition-colors"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || total === 0}
            data-testid="export-leads"
            className="h-9 px-3.5 rounded-[10px] border border-b2-line bg-b2-surface text-[13.5px] font-medium text-b2-ink
                       hover:bg-black/[0.02] disabled:opacity-40 disabled:pointer-events-none
                       inline-flex items-center gap-1.5 transition-colors"
          >
            {exporting
              ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
              : <Download className="w-4 h-4" strokeWidth={1.8} />}
            Export
          </button>
        </div>
      </div>

      {/* ----------------------------- search + chips ---------------------------- */}
      <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="lead-status-pills">
        <div className="relative w-full sm:w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-b2-faint" strokeWidth={1.8} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search contacts"
            data-testid="search-leads"
            className="w-full h-9 pl-9 pr-3 rounded-[10px] bg-black/[0.03] border border-transparent text-[13.5px]
                       focus:bg-white focus:border-b2-indigo focus:outline-none transition"
          />
        </div>

        <button
          type="button"
          onClick={() => setFilterStatus("all")}
          data-testid="pill-all"
          className={`h-9 px-3.5 rounded-full text-[13px] font-medium transition-colors ${
            filterStatus === "all" ? "bg-b2-wash text-b2-washink" : "text-b2-muted hover:text-b2-ink hover:bg-black/[0.03]"
          }`}
        >
          All <span className="tabular-nums opacity-70">{total}</span>
        </button>
        <button
          type="button"
          onClick={() => setFilterStatus(filterStatus === "new" ? "all" : "new")}
          data-testid="pill-new"
          className={`h-9 px-3.5 rounded-full text-[13px] font-medium transition-colors ${
            filterStatus === "new" ? "bg-b2-wash text-b2-washink" : "text-b2-muted hover:text-b2-ink hover:bg-black/[0.03]"
          }`}
        >
          New <span className="tabular-nums opacity-70">{statusCounts.new || 0}</span>
        </button>

        <div className="ml-auto">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="leads-filters"
                className={`h-9 px-3.5 rounded-[10px] border text-[13.5px] font-medium inline-flex items-center gap-1.5 transition-colors ${
                  filtersOn
                    ? "border-b2-indigo text-b2-indigo bg-b2-wash"
                    : "border-b2-line bg-b2-surface text-b2-muted hover:text-b2-ink"
                }`}
              >
                <SlidersHorizontal className="w-4 h-4" strokeWidth={1.8} /> Filters
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 rounded-[14px] p-4 space-y-4">
              <div className="space-y-1.5">
                <label className="text-[12.5px] font-medium text-b2-inksoft">Assistant</label>
                <select
                  value={filterBot}
                  onChange={(e) => setFilterBot(e.target.value)}
                  data-testid="filter-chatbot"
                  className="w-full h-9 px-2.5 rounded-[10px] border border-b2-line text-[13.5px] bg-white"
                >
                  <option value="all">All assistants</option>
                  {chatbots.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>

              <div className="space-y-1.5" data-testid="filter-status">
                <label className="text-[12.5px] font-medium text-b2-inksoft">Status</label>
                <div className="space-y-0.5">
                  <button
                    type="button"
                    onClick={() => setFilterStatus("all")}
                    className={`w-full flex items-center justify-between px-2.5 h-8 rounded-[10px] text-[13.5px] transition-colors ${
                      filterStatus === "all" ? "bg-b2-wash text-b2-washink font-medium" : "text-b2-inksoft hover:bg-black/[0.03]"
                    }`}
                  >
                    <span>All statuses</span>
                    <span className="tabular-nums text-b2-faint">{leads.length}</span>
                  </button>
                  {STATUSES.map((p) => (
                    <button
                      key={p.k}
                      type="button"
                      onClick={() => setFilterStatus(p.k === filterStatus ? "all" : p.k)}
                      data-testid={`pill-${p.k}`}
                      className={`w-full flex items-center justify-between px-2.5 h-8 rounded-[10px] text-[13.5px] transition-colors ${
                        filterStatus === p.k ? "bg-b2-wash text-b2-washink font-medium" : "text-b2-inksoft hover:bg-black/[0.03]"
                      }`}
                    >
                      <span>{p.label}</span>
                      <span className="tabular-nums text-b2-faint">{statusCounts[p.k] || 0}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-1 border-t border-b2-line">
                <button
                  type="button"
                  onClick={() => setTaggedOnly(v => !v)}
                  title="Show only contacts that have tags"
                  data-testid="tagged-only-toggle"
                  className={`w-full flex items-center justify-between px-2.5 h-9 mt-2 rounded-[10px] text-[13.5px] transition-colors ${
                    taggedOnly ? "bg-b2-wash text-b2-washink font-medium" : "text-b2-inksoft hover:bg-black/[0.03]"
                  }`}
                >
                  <span>Tagged only</span>
                  <span className={`w-4 h-4 rounded-[5px] border flex items-center justify-center ${
                    taggedOnly ? "bg-b2-indigo border-b2-indigo" : "border-b2-linestrong"
                  }`}>
                    {taggedOnly && <span className="w-1.5 h-1.5 rounded-[2px] bg-white" />}
                  </span>
                </button>
              </div>

              {filtersOn && (
                <button
                  type="button"
                  onClick={() => { setFilterBot("all"); setFilterStatus("all"); setTaggedOnly(false); }}
                  className="text-[13px] text-b2-indigo hover:underline"
                >Clear filters</button>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {visibleIds.length > 0 && filterStatus !== "all" && (
        <div className="mt-3 flex items-center gap-2" data-testid="leads-quick-filter-actions">
          <button type="button" onClick={() => setSelectedIds(visibleIds)} className="text-[13px] text-b2-indigo hover:underline" data-testid="select-all-in-filter">
            Select all {visibleIds.length} {filterStatus} contact{visibleIds.length === 1 ? "" : "s"}
          </button>
        </div>
      )}

      {/* --------------------------------- table --------------------------------- */}
      <div className="mt-5 b2-card overflow-hidden">
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap px-6 py-3 border-b border-b2-line bg-b2-wash">
            <span className="text-[13px] font-medium text-b2-washink">{selectedIds.length} selected</span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="h-8 px-3 rounded-[10px] border border-b2-line bg-b2-surface text-[13px] text-b2-inksoft hover:text-b2-ink"
              >Clear</button>
              <select
                onChange={(e) => { if (e.target.value) { handleBulkStatus(e.target.value); e.target.value = ""; } }}
                className="h-8 px-2 rounded-[10px] border border-b2-line bg-b2-surface text-[13px] text-b2-inksoft"
                data-testid="bulk-status-select"
                defaultValue=""
              >
                <option value="">Set status…</option>
                {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
              </select>
              <button
                type="button"
                onClick={handleBulkTag}
                data-testid="bulk-tag-btn"
                className="h-8 px-3 rounded-[10px] border border-b2-line bg-b2-surface text-[13px] text-b2-inksoft hover:text-b2-ink"
              >Tag…</button>
              <button
                type="button"
                onClick={handleBulkDelete}
                className="h-8 px-3 rounded-[10px] text-[13px] font-medium text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.8} /> Delete
              </button>
            </div>
          </div>
        )}

        {bulkTagOpen && (
          <div className="flex items-center gap-2 px-6 py-3 border-b border-b2-line bg-b2-wash" data-testid="bulk-tag-dialog">
            <span className="text-[13px] font-medium text-b2-washink shrink-0">Add tags to {selectedIds.length} contact(s):</span>
            <input
              type="text"
              autoFocus
              value={bulkTagInput}
              onChange={(e) => setBulkTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitBulkTag(); if (e.key === "Escape") setBulkTagOpen(false); }}
              placeholder="vip, hot-lead, priority"
              className="flex-1 h-8 px-2.5 rounded-[10px] border border-b2-linestrong bg-white text-[13px] focus:border-b2-indigo focus:outline-none"
              data-testid="bulk-tag-input"
            />
            <button
              type="button"
              onClick={submitBulkTag}
              data-testid="bulk-tag-submit"
              className="h-8 px-3 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover text-white text-[13px] font-medium"
            >Apply</button>
            <button
              type="button"
              onClick={() => setBulkTagOpen(false)}
              className="h-8 px-3 rounded-[10px] border border-b2-line bg-b2-surface text-[13px] text-b2-inksoft hover:text-b2-ink"
            >Cancel</button>
          </div>
        )}

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-b2-faint" /></div>
        ) : shownLeads.length === 0 ? (
          <div className="px-8 py-20 flex flex-col items-center text-center">
            <Users className="w-8 h-8 text-b2-faint" strokeWidth={1.5} />
            <p className="mt-4 text-[16px] font-bold tracking-[-0.03em] text-b2-ink">No contacts yet</p>
            <p className="mt-1.5 text-[13.5px] text-b2-muted max-w-sm">
              As soon as Bicarra captures someone's details, they will appear here — ready to follow up.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-b2-line">
                  <th className="w-10 pl-6 pr-2 py-3 text-left">
                    <Checkbox checked={allSelected} onCheckedChange={() => toggleAll(visibleIds)} data-testid="leads-select-all" />
                  </th>
                  <th className="px-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Name</th>
                  <th className="px-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Assistant</th>
                  <th className="px-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Status</th>
                  <th className="px-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Added</th>
                  <th className="w-24 pl-2 pr-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-b2-line">
                {shownLeads.map(lead => (
                  <tr key={lead.id} className={`transition-colors ${selectedIds.includes(lead.id) ? "bg-b2-wash" : "hover:bg-black/[0.015]"}`}>
                    <td className="w-10 pl-6 pr-2 py-4 align-middle">
                      <Checkbox checked={selectedIds.includes(lead.id)} onCheckedChange={() => toggleSelect(lead.id)} data-testid={`lead-select-${lead.id}`} />
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-8 h-8 rounded-full bg-black/[0.05] text-b2-inksoft flex items-center justify-center text-[12px] font-semibold shrink-0">
                          {initialOf(lead)}
                        </span>
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[14px] font-medium text-b2-ink truncate">
                              {lead.visitor_data?.name || "Anonymous"}
                            </span>
                            {lead.tags && lead.tags.length > 0 && lead.tags.slice(0, 3).map((tg) => (
                              <span key={tg} className="px-1.5 py-0.5 rounded-full bg-black/[0.04] text-[11px] text-b2-muted">{tg}</span>
                            ))}
                          </span>
                          <span className="flex items-center gap-3 mt-0.5 text-[12.5px] text-b2-muted">
                            {lead.visitor_data?.email && (
                              <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" strokeWidth={1.8} /> {lead.visitor_data.email}</span>
                            )}
                            {lead.visitor_data?.phone && (
                              <span className="flex items-center gap-1 truncate"><Phone className="w-3 h-3 shrink-0" strokeWidth={1.8} /> {lead.visitor_data.phone}</span>
                            )}
                            {!lead.visitor_data?.email && !lead.visitor_data?.phone && <span className="text-b2-faint">—</span>}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 align-middle text-[13.5px] text-b2-inksoft">{getChatbotName(lead.chatbot_id)}</td>
                    <td className="px-4 py-4 align-middle">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[12px] font-medium capitalize ${statusPillClass(lead.status || "new")}`}>
                        {lead.status || "new"}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className="block text-[13.5px] text-b2-inksoft">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}</span>
                      <span className="block text-[12px] text-b2-faint">{lead.created_at ? new Date(lead.created_at).toLocaleTimeString() : ""}</span>
                    </td>
                    <td className="w-24 pl-2 pr-6 py-4 align-middle">
                      <div className="flex items-center justify-end gap-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              title="More"
                              data-testid={`lead-menu-${lead.id}`}
                              className="w-8 h-8 rounded-[10px] flex items-center justify-center text-b2-faint hover:text-b2-ink hover:bg-black/[0.04] transition-colors"
                            >
                              <MoreHorizontal className="w-4 h-4" strokeWidth={1.8} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56 rounded-[14px]">
                            <DropdownMenuItem onClick={() => { setLeadChat(null); setViewLead(lead); }} data-testid="view-lead"><UserIcon className="w-4 h-4 mr-2" strokeWidth={1.8} /> View details</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEdit(lead)}><UserIcon className="w-4 h-4 mr-2" strokeWidth={1.8} /> Edit notes & tags</DropdownMenuItem>
                            {STATUSES.map((s) => (
                              <DropdownMenuItem key={s.k} onClick={() => handleStatusChange(lead, s.k)}>Mark as {s.label}</DropdownMenuItem>
                            ))}
                            <DropdownMenuItem onClick={() => handleDelete(lead.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" strokeWidth={1.8} /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <button
                          type="button"
                          onClick={() => { setLeadChat(null); setViewLead(lead); }}
                          title="Open contact"
                          data-testid={`lead-open-${lead.id}`}
                          className="w-8 h-8 rounded-[10px] flex items-center justify-center text-b2-faint hover:text-b2-ink hover:bg-black/[0.04] transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" strokeWidth={1.8} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div className="flex items-center justify-between px-6 py-3.5 border-t border-b2-line">
            <span className="text-[13px] text-b2-muted">Showing {shownLeads.length} of {total}</span>
          </div>
        )}
      </div>

      {/* ------------------------- supporting panels ------------------------- */}
      {sources && sources.length > 0 && (
        <div className="mt-5 b2-card p-5">
          <h2 className="text-[16px] font-bold tracking-[-0.03em] text-b2-ink">Where they came from</h2>
          <p className="mt-1 text-[13px] text-b2-muted">How visitors are reaching your assistants.</p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="lead-sources">
            {sources.slice(0, 8).map((sr) => (
              <div key={sr.source} className="flex items-center gap-2 px-3 h-8 rounded-[10px] bg-black/[0.03]">
                <span className="text-[13px] text-b2-inksoft">{sr.source}</span>
                <span className="text-[12px] tabular-nums text-b2-muted">{sr.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {funnel && funnel.total > 0 && (
        <div className="mt-5 b2-card p-5">
          <h2 className="text-[16px] font-bold tracking-[-0.03em] text-b2-ink">Pipeline</h2>
          <p className="mt-1 text-[13px] text-b2-muted">Status distribution over the last 30 days.</p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="leads-funnel">
            {STATUSES.map(st => {
              const v = funnel[st.k] || 0;
              const pct = funnel.total ? Math.round((v / funnel.total) * 100) : 0;
              return (
                <div key={st.k} className="rounded-[10px] bg-black/[0.02] px-3 py-3">
                  <p className="text-[12.5px] text-b2-muted">{st.label}</p>
                  <p className="mt-0.5 text-[20px] font-bold tracking-[-0.03em] text-b2-ink tabular-nums">{v}</p>
                  <p className="text-[12px] text-b2-faint tabular-nums">{pct}%</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {timeline && timeline.length > 0 && (
        <div className="mt-5 b2-card p-5">
          <h2 className="text-[16px] font-bold tracking-[-0.03em] text-b2-ink">Captured per day</h2>
          <p className="mt-1 text-[13px] text-b2-muted">Contacts captured over the last 30 days.</p>
          {(() => {
            const items = timeline;
            const maxV = Math.max(1, ...items.map(d => d.total || 0));
            return (
              <div className="mt-4" data-testid="leads-timeline">
                <div className="flex items-end gap-1 h-24">
                  {items.map((d, i) => {
                    const h = ((d.total || 0) / maxV) * 100;
                    const cHeight = d.total ? Math.max(((d.converted || 0) / d.total) * h, 0) : 0;
                    const qHeight = d.total ? Math.max(((d.qualified || 0) / d.total) * h, 0) : 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col justify-end min-w-0" title={`${d.date}: ${d.total} leads (${d.qualified} qualified, ${d.converted} converted)`}>
                        <div className="bg-b2-live rounded-t-sm" style={{ height: `${cHeight}%` }} />
                        <div className="bg-b2-attention" style={{ height: `${qHeight}%` }} />
                        <div className="bg-b2-indigo rounded-b-sm" style={{ height: `${Math.max(h - cHeight - qHeight, 0)}%` }} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[12px] text-b2-faint mt-2.5">
                  <span>{items[0]?.date || ""}</span>
                  <span className="flex items-center gap-3">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-b2-indigo inline-block rounded-sm" />New</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-b2-attention inline-block rounded-sm" />Qualified</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-b2-live inline-block rounded-sm" />Converted</span>
                  </span>
                  <span>{items[items.length - 1]?.date || ""}</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {allTags && allTags.length > 0 && (
        <div className="mt-5 b2-card p-5" data-testid="workspace-tags">
          <h2 className="text-[16px] font-bold tracking-[-0.03em] text-b2-ink">Tags in this workspace</h2>
          <p className="mt-1 text-[13px] text-b2-muted">Click a tag to search for it.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {allTags.slice(0, 60).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => { setSearch(t); }}
                className="h-8 px-3 rounded-[10px] bg-black/[0.03] text-[13px] text-b2-inksoft hover:bg-black/[0.06] transition-colors"
              >#{t}</button>
            ))}
          </div>
        </div>
      )}

      {/* ---------------------------- contact detail ---------------------------- */}
      {viewLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setViewLead(null)} data-testid="view-lead-modal">
          <div className="bg-b2-surface rounded-[14px] border border-b2-line shadow-lg w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-10 h-10 rounded-full bg-black/[0.05] text-b2-inksoft flex items-center justify-center text-[14px] font-semibold shrink-0">
                  {initialOf(viewLead)}
                </span>
                <div className="min-w-0">
                  <h3 className="text-[20px] font-bold tracking-[-0.03em] text-b2-ink truncate">
                    {viewLead.visitor_data?.name || viewLead.visitor_data?.email || "Anonymous contact"}
                  </h3>
                  <p className="text-[12.5px] text-b2-faint">Added {viewLead.created_at ? new Date(viewLead.created_at).toLocaleString() : "—"}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setViewLead(null)}
                className="h-8 px-3 rounded-[10px] border border-b2-line text-[13px] text-b2-inksoft hover:text-b2-ink shrink-0"
              >Close</button>
            </div>

            <div className="space-y-3 text-[13.5px]">
              <div className="flex items-center gap-2">
                <span className="text-b2-muted">Status</span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[12px] font-medium capitalize ${statusPillClass(viewLead.status || "new")}`}>
                  {viewLead.status || "new"}
                </span>
                <select
                  value={viewLead.status || "new"}
                  onChange={(e) => handleStatusChange(viewLead, e.target.value)}
                  data-testid="view-lead-status"
                  className="ml-auto h-8 px-2 rounded-[10px] border border-b2-line bg-white text-[13px] text-b2-inksoft"
                >
                  {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                </select>
              </div>
              {viewLead.visitor_data?.email && <div><span className="text-b2-muted">Email:</span> <span className="text-b2-ink">{viewLead.visitor_data.email}</span></div>}
              {viewLead.visitor_data?.phone && <div><span className="text-b2-muted">Phone:</span> <span className="text-b2-ink">{viewLead.visitor_data.phone}</span></div>}
              {Object.entries(viewLead.visitor_data || {}).filter(([k]) => !['name','email','phone'].includes(k)).map(([k,v]) => (
                <div key={k}><span className="text-b2-muted">{k}:</span> <span className="text-b2-ink">{String(v)}</span></div>
              ))}
              {viewLead.source && Object.keys(viewLead.source || {}).length > 0 && (
                <div className="pt-3 border-t border-b2-line">
                  <p className="text-[12.5px] font-medium text-b2-inksoft mb-1.5">Source</p>
                  {Object.entries(viewLead.source).map(([k,v]) => (
                    <div key={k} className="text-[13px]"><span className="text-b2-muted">{k}:</span> <span className="text-b2-ink">{String(v)}</span></div>
                  ))}
                </div>
              )}
              {Array.isArray(viewLead.tags) && viewLead.tags.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-b2-muted">Tags:</span>
                  {viewLead.tags.map((tg) => (
                    <span key={tg} className="px-1.5 py-0.5 rounded-full bg-black/[0.04] text-[11px] text-b2-muted">{tg}</span>
                  ))}
                </div>
              )}

              <div className="pt-3 border-t border-b2-line flex items-center gap-4 text-[12.5px] text-b2-muted" data-testid="lead-source-strip">
                <span><span className="text-b2-faint">Assistant:</span> {(chatbots.find(b => b.id === viewLead.chatbot_id) || {}).name || "—"}</span>
                <span><span className="text-b2-faint">Age:</span> {viewLead.created_at ? (() => { const m = Math.max(0, Math.round((Date.now() - new Date(viewLead.created_at).getTime()) / 60000)); if (m < 60) return m + "m ago"; const h = Math.round(m/60); if (h < 24) return h + "h ago"; return Math.round(h/24) + "d ago"; })() : "—"}</span>
                <span><span className="text-b2-faint">Tags:</span> {Array.isArray(viewLead.tags) && viewLead.tags.length > 0 ? viewLead.tags.length : 0}</span>
              </div>

              {viewLead.notes && (
                <div className="pt-3 border-t border-b2-line">
                  <p className="text-[12.5px] font-medium text-b2-inksoft mb-1.5">Notes</p>
                  <p className="text-[13px] text-b2-ink whitespace-pre-wrap">{viewLead.notes}</p>
                </div>
              )}

              <div className="pt-3 border-t border-b2-line">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setLeadChatLoading(true);
                      const res = await leadsApi.conversation(viewLead.id);
                      setLeadChat(res.data);
                    } catch (e) {
                      toast.error("Could not load conversation");
                    } finally {
                      setLeadChatLoading(false);
                    }
                  }}
                  data-testid="lead-view-chat"
                  className="h-8 px-3 rounded-[10px] border border-b2-line text-[13px] text-b2-inksoft hover:text-b2-ink"
                >
                  {leadChatLoading ? "Loading…" : "View chat history"}
                </button>
                {leadChat && leadChat.messages && leadChat.messages.length > 0 && (
                  <div className="mt-3 max-h-64 overflow-y-auto rounded-[10px] bg-black/[0.02] p-3 space-y-2">
                    {leadChat.messages.map((m) => (
                      <div key={m.id} className="text-[13px]">
                        <span className="font-semibold text-b2-ink">
                          {m.role === "user"
                            ? (viewLead.visitor_data?.name || "Visitor")
                            : (m.role === "agent" ? "Agent" : "Bot")}:
                        </span>{" "}
                        <span className="whitespace-pre-wrap text-b2-inksoft">{m.content}</span>
                      </div>
                    ))}
                  </div>
                )}
                {leadChat && (!leadChat.messages || leadChat.messages.length === 0) && (
                  <p className="mt-2 text-[13px] text-b2-muted">No conversation found for this contact.</p>
                )}
              </div>

              <div className="pt-3 border-t border-b2-line">
                <p className="text-[12.5px] font-medium text-b2-inksoft mb-2">Notes timeline</p>
                <div className="flex gap-2 mb-3">
                  <textarea
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder="Add a note…"
                    rows={2}
                    className="flex-1 text-[13px] rounded-[10px] border border-b2-linestrong px-2.5 py-2 resize-none focus:border-b2-indigo focus:outline-none"
                    data-testid="lead-note-input"
                  />
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={savingNote || !noteInput.trim()}
                    data-testid="lead-note-add"
                    className="h-8 px-3 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover text-white text-[13px] font-medium disabled:opacity-40 disabled:pointer-events-none self-start"
                  >Add</button>
                </div>
                {leadNotes.length === 0 ? (
                  <p className="text-[13px] text-b2-faint">No notes yet.</p>
                ) : (
                  <ul className="space-y-2 max-h-64 overflow-y-auto">
                    {leadNotes.map((n) => (
                      <li key={n.id} className="rounded-[10px] bg-black/[0.02] p-2.5" data-testid={`lead-note-${n.id}`}>
                        <div className="flex justify-between items-start gap-2">
                          <p className="text-[13px] text-b2-ink whitespace-pre-wrap flex-1">{n.text}</p>
                          <button type="button" onClick={() => handleDeleteNote(n.id)} className="text-b2-faint hover:text-red-600 shrink-0" title="Delete note">
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                          </button>
                        </div>
                        <div className="text-[11.5px] text-b2-faint mt-1">{n.author_email || "—"} · {new Date(n.created_at).toLocaleString()}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------ edit contact ------------------------------ */}
      {editLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !savingEdit && setEditLead(null)}>
          <div className="bg-b2-surface rounded-[14px] border border-b2-line shadow-lg w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[20px] font-bold tracking-[-0.03em] text-b2-ink">Edit contact</h3>
            <p className="mt-1 text-[13px] text-b2-muted mb-5">{editLead.visitor_data?.name || editLead.visitor_data?.email || "Anonymous"}</p>

            <label className="block text-[12.5px] font-medium text-b2-inksoft mb-1.5">Internal notes</label>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={5}
              className="w-full rounded-[10px] border border-b2-linestrong px-3 py-2 text-[13.5px] mb-4 focus:border-b2-indigo focus:outline-none"
              placeholder="Add notes about this contact (visible only to your team)"
            />

            <label className="block text-[12.5px] font-medium text-b2-inksoft mb-1.5">Tags (comma-separated)</label>
            <input
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              placeholder="hot, follow-up, vip"
              className="w-full h-9 rounded-[10px] border border-b2-linestrong px-3 text-[13.5px] focus:border-b2-indigo focus:outline-none"
            />

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setEditLead(null)}
                disabled={savingEdit}
                className="h-9 px-4 rounded-[10px] border border-b2-line text-[13.5px] font-medium text-b2-inksoft hover:text-b2-ink disabled:opacity-40"
              >Cancel</button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={savingEdit}
                className="h-9 px-4 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover text-white text-[13.5px] font-medium disabled:opacity-40"
              >{savingEdit ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
